import React, { useCallback, useLayoutEffect } from 'react';
import { View } from 'react-native';
import * as RNLocalize from 'react-native-localize';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, ButtonGroup, CurrencyAmountText, rem, SCROLL_INDICATOR_INSETS, Spacer } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import EmojiText from 'design-system/EmojiText';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import Squircle from 'design-system/Squircle';
import StatusBar from 'design-system/StatusBar';
import StatusIcon from 'design-system/StatusIcon';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { isWeb } from '../../../constants';
import BankWithdrawInfoCard from '../components/WithdarTimeline/BankWithdrawInfoCard';
import EmmaBalanceInfoCard from '../components/WithdarTimeline/EmmaBalanceInfoCard';
import WithdrawRequestedCard from '../components/WithdarTimeline/WithdrawRequestedCard';
import { selectPotProductFromProductId } from '../reducers/selectors';
const isUKTimezone = RNLocalize.getTimeZone() === 'Europe/London';
const WithdrawTimeline = withAnimatedNavigationHeader(({ scrollY, navigation, route }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => (<NavigationCloseButton inWebHeader onPress={() => {
                    navigation.pop();
                }}/>),
            headerBackVisible: false,
        });
    }, []);
    const { emoji, potName, bankName, productId, dateTimeString, isBankTransfer, withdrawAmount, formattedInitiatedTime, } = route.params;
    const estimatedTimeInfo = `Estimated by ${dateTimeString}${isUKTimezone ? '' : ' UK time'}`;
    const noticeDays = useAppSelector((state) => selectPotProductFromProductId(state, productId))?.noticeDays;
    const isMultibank = useAppSelector((state) => selectPotProductFromProductId(state, productId))?.isMultibank;
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    });
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(16), undefined, paddingHorizontalStyle);
    const handleClose = useCallback(() => {
        navigation.goBack();
    }, []);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} keyboardDismissMode="on-drag" contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <Spacer h={24}/>
          <View style={styles.amountInfo}>
            <View>
              <View style={styles.emojiContainer}>
                <View style={styles.emojiContainerInner}>
                  <EmojiText fontSize={rem(26)}>{emoji}</EmojiText>
                </View>
              </View>
              <View style={styles.statusIconContainer}>
                <Squircle width={rem(24)} height={rem(24)} color={colors.cards.onDark}>
                  <StatusIcon state={'in-progress'} height={20} width={20}/>
                </Squircle>
              </View>
            </View>
            <Spacer h={24}/>
            <Text Text-16 Secondary>
              Withdrawal request
            </Text>
            <Spacer h={8}/>
            <CurrencyAmountText size="XL" value={{ amount: withdrawAmount, currency: 'GBP' }}/>
          </View>
          <Spacer h={8}/>
          <WithdrawRequestedCard noticeDays={noticeDays} formattedInitiatedTime={formattedInitiatedTime} bankName={isMultibank ? bankName.toLowerCase() : bankName}/>
          <BankWithdrawInfoCard potName={potName} noticeDays={noticeDays}/>
          <EmmaBalanceInfoCard dateTimeString={estimatedTimeInfo} isBankTransfer={isBankTransfer}/>
          <Spacer h={24}/>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button title="Close" brand onPress={handleClose}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    title: ``,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    amountInfo: {
        alignItems: 'center',
    },
    emojiContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    emojiContainerInner: {
        width: rem(52),
        height: rem(52),
        borderRadius: rem(52),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.dark,
    },
    statusIconContainer: {
        right: 0,
        bottom: 0,
        position: 'absolute',
    },
}));
export default WithdrawTimeline;
